import {
  PROTOCOL_VERSION,
  validateGameDefinition,
  type BoardDef,
  type GameState,
  type GameDefinition,
} from '@archon3d/core';
import type { GameSetup } from '../ui/gameSetup';

/**
 * The running local game as kept in this browser, enough to put it back on the table: the
 * setup (board, seats, how it runs), the seed and the rules state. Online games are the
 * server's to keep, so they are never saved here.
 */
export interface SavedGame {
  definition?: GameDefinition;
  version: 1;
  setup: GameSetup;
  seed: number;
  state: GameState;
  /** Freeze the board used by this save rather than resolving a subsequently edited board. */
  board?: BoardDef;
  /** Milliseconds since the epoch, for "last played" and for choosing the newest of two. */
  savedAt: number;
  /**
   * The rules the game was kept under (`SAVE_RULES`): a save that no longer loads is told of as
   * one from an older version, not dropped without a word (client-session:quality#10).
   */
  rules?: number;
}

/** The protocol's version moves with every change to what a game's state means. */
export const SAVE_RULES = PROTOCOL_VERSION;

/** Why the game kept in this browser was not put back on the table. */
export function lostSaveText(game: SavedGame | null): string {
  return game && game.rules !== SAVE_RULES
    ? 'The game saved in this browser was kept by an older version, and could not be continued.'
    : 'The game saved in this browser could not be read, and could not be continued.';
}

export interface SaveStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const SAVE_KEY = 'archon3d.game';

function browserStorage(): SaveStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** The saved game, or `null` when there is none or it cannot be read. */
export function loadSavedGame(storage: SaveStorage | null = browserStorage()): SavedGame | null {
  try {
    const text = storage?.getItem(SAVE_KEY);
    if (!text) return null;
    const raw = JSON.parse(text) as Partial<SavedGame>;
    if (
      raw.version !== 1 ||
      !raw.setup ||
      typeof raw.seed !== 'number' ||
      !raw.state ||
      typeof raw.state !== 'object' ||
      !Array.isArray(raw.state.pieces)
    )
      return null;
    if (raw.definition) {
      const definition = validateGameDefinition(raw.definition);
      if (
        definition.id !== raw.setup.boardId ||
        (raw.board && JSON.stringify(raw.board) !== JSON.stringify(definition.board))
      )
        return null;
    }
    return raw as SavedGame;
  } catch {
    return null;
  }
}

/** Whether anything is kept under the save's key, readable or not. */
export function hasSavedGame(storage: SaveStorage | null = browserStorage()): boolean {
  try {
    return Boolean(storage?.getItem(SAVE_KEY));
  } catch {
    return false;
  }
}

export function saveGame(game: SavedGame, storage: SaveStorage | null = browserStorage()): void {
  try {
    storage?.setItem(SAVE_KEY, JSON.stringify({ ...game, rules: SAVE_RULES }));
  } catch {
    // A full or blocked store: the game simply is not kept.
  }
}

export function clearSavedGame(storage: SaveStorage | null = browserStorage()): void {
  try {
    storage?.removeItem(SAVE_KEY);
  } catch {
    // Nothing to clear.
  }
}

/** Whether a saved game is worth continuing: it has begun and has not ended. */
export function canContinue(game: SavedGame | null): game is SavedGame {
  return game !== null && game.state.phase !== 'ended';
}
